package com.fantasytenis.web.components

import androidx.compose.runtime.Composable
import com.fantasytenis.shared.domain.lineup.entity.Category
import com.fantasytenis.web.theme.ThemeColors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

/**
 * Visual card displaying scoring information for a lineup category.
 * Used in the "How to Play" section to explain scoring rules.
 */
@Composable
fun ScoringCategoryCard(
    category: Category,
    positivePoints: String,
    negativePoints: String?,
    colors: ThemeColors
) {
    Div({
        style {
            backgroundColor(colors.surfaceVariant)
            borderRadius(8.px)
            padding(12.px)
            textAlign("center")
            minWidth(100.px)
            flex(1)
        }
    }) {
        // Category icon and name
        Div({
            style {
                fontSize(20.px)
                marginBottom(4.px)
            }
        }) {
            Text(getCategoryIcon(category))
        }

        Div({
            style {
                fontSize(11.px)
                fontWeight(600)
                color(colors.textPrimary)
                marginBottom(8.px)
                property("text-transform", "uppercase")
                letterSpacing(0.5.px)
            }
        }) {
            Text(category.displayName)
        }

        // Positive points (green)
        Div({
            style {
                fontSize(12.px)
                color(colors.success)
                fontWeight(500)
            }
        }) {
            Text(positivePoints)
        }

        // Negative points (red) - if any
        negativePoints?.let { negative ->
            Div({
                style {
                    fontSize(12.px)
                    color(colors.error)
                    fontWeight(500)
                    marginTop(2.px)
                }
            }) {
                Text(negative)
            }
        }
    }
}

/**
 * Get emoji icon for a scoring category.
 */
fun getCategoryIcon(category: Category): String {
    return when (category) {
        Category.SERVE -> "🎾"
        Category.FOREHAND -> "💪"
        Category.BACKHAND -> "🏸"
        Category.VOLLEYS -> "🏐"
        Category.FIGHTING -> "⚡"
    }
}

/**
 * Get the color associated with a category for visual differentiation.
 */
@Composable
fun getCategoryColor(category: Category, colors: ThemeColors): CSSColorValue {
    return when (category) {
        Category.SERVE -> colors.info          // Blue
        Category.FOREHAND -> colors.success    // Green
        Category.BACKHAND -> colors.warning    // Orange
        Category.VOLLEYS -> colors.accent      // Light blue
        Category.FIGHTING -> colors.error      // Red
    }
}
